import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    IMediaCollectionViewState,
    IMediaCollectionView
} from "../../type/media-hub.types";
import ImagePreviewModal from "../../utils/image-preview-modal.view";
import ScrollPagination from "../../../common/scroll-pagination/scroll-pagination";
import ImageCard from "../../utils/image-card.view";
import MediaHubToolbar from "../../utils/media-hub-toolbar.view";
import { getImageUrl } from "../../../../utility/utility";
import UploadDocumentModal from "../../../common/upload-document-modal/upload-document-modal";
import { Document } from "../../../../domains/models/document";
import { Document_Type } from "../../../../constant/constant";
import EmptyData from "../../../common/empty-data/empty-data";
import UserLoginContext from "../../../../context/userLoginContext";
import { Button } from "antd";
import { ArrowLeft } from "lucide-react";
import { emptyStates, getDocumentKey } from "../../utils/utils";

const MediaCollectionView: React.FC<IMediaCollectionView> = (props) => {
    const currentState = emptyStates[props.categorySelected as keyof typeof emptyStates] ?? emptyStates['Others'];
    const navigate = useNavigate();
    const {user} = useContext(UserLoginContext);


    const initialState = {
        selectedIds: new Set<string>(),
        currentDocId: '',
        previewModal: {
            isOpen: false,
            imgDetails: null,
        },
        uploadFileModal: {
            isOpen: false
        }
    }
    const folderName = useLocation().state?.eventName ?? null;
    const [state, setState] = useState<IMediaCollectionViewState>(initialState);




    const handlePreviewModalOpen = (item: Document) => {
        setState((prev) => ({
            ...prev,
            currentDocId: item.id,
            previewModal: {
                isOpen: true,
                imgDetails: item
            }
        }));

    };

    function handleShowDetails(item: Document) {

        setState((prev) => ({
            ...prev,
            currentDocId: item.id,
            previewModal: {
                isOpen: true,
                imgDetails: item,
                showInfo: true
            }
        }));
    }


    const handlePreviewModalClose = () => {
        setState((prev) => ({
            ...prev,
            currentDocId: '',
            previewModal: {
                isOpen: false,
                imgDetails: null
            },
        }));
    };

    function handleMediaUploadOpen() {
        setState((prev) => ({
            ...prev,
            uploadFileModal: {
                isOpen: true
            }
        }))
    }

    function handleMediaUploadClose() {
        setState((prev) => ({
            ...prev,
            uploadFileModal: {
                isOpen: false
            }
        }))
    }


    function toggleCheckBoxState(id: string) {
        setState(prev => {
            const nextSelectedIds = new Set(prev.selectedIds);

            if (nextSelectedIds.has(id)) {
                nextSelectedIds.delete(id);
            } else {
                nextSelectedIds.add(id);
            }

            return {
                ...prev,
                selectedIds: nextSelectedIds
            };
        });
    }

    function goToNextImage() {
        const currentIndex = props.files.findIndex(
            (file) => file.id === state.currentDocId
        );

        if (currentIndex === -1 || currentIndex === props.files.length - 1) {
            return;
        }

        const nextFile = props.files[currentIndex + 1];

        setState((prev) => ({
            ...prev,
            currentDocId: nextFile.id,
            previewModal: {
                isOpen: true,
                imgDetails: nextFile
            },
        }));
    }

    function goToPreviousImage() {
        const currentIndex = props.files.findIndex(
            (file) => file.id === state.currentDocId
        );

        if (currentIndex === 0) {
            return;
        }

        const prevFile = props.files[currentIndex - 1];
        if (prevFile)
            setState((prev) => ({
                ...prev,
                currentDocId: prevFile.id,
                previewModal: {
                    isOpen: true,
                    imgDetails: prevFile
                },
            }));
    }


    useEffect(() => {
        if (!state.previewModal.isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight") {
                goToNextImage();
            }
            if (e.key === "ArrowLeft") {
                goToPreviousImage();
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => {
            // Remove event listener when modal closes
            window.removeEventListener("keydown", handleKey);
        };
    }, [state.previewModal.isOpen, state.currentDocId]);


    const isSelected = (id: string): boolean => {
        return state.selectedIds.has(id);
    };

    const setSelectedIds = (ids: Set<string>) => {
        setState((prev) => ({
            ...prev,
            selectedIds: ids
        }));
    }

    function handleBackNavigation() {
        navigate(`/highlights/media-hub/${props.categorySelected.toLowerCase()}`);
    }


    const docType = getDocumentKey(props.categorySelected);


    return (
        <div className="media-gallery">
            <div className="media-gallery__header">

                {folderName &&
                (

                    <div className="media-gallery__header-left">
                        <button className="media-gallery__back-button" onClick={handleBackNavigation}><ArrowLeft/>
                        </button>
                        <h4 className="media-gallery__title">{folderName}</h4>
                    </div>

                )}

                <MediaHubToolbar
                    files={props.files}
                    isAdmin={props.isAdmin}
                    setSelectedIds={setSelectedIds}
                    selectedIds={state.selectedIds}
                    handleMediaUploadOpen={handleMediaUploadOpen}
                    handleMultipleDelete={props.handleMultipleDelete}
                    handleMultipleDownload={props.handleMultipleDownload}
                    categorySelected={props.categorySelected}
                />
            </div>

            <ScrollPagination
                loadNext={props.paginationControls.loadNext}
                loadPrevious={props.paginationControls.loadPrevious}
                hasNext={props.paginationControls.hasNext}
                hasPrevious={props.paginationControls.hasPrevious}
                isLoading={props.paginationControls.isLoading}
                className={'image-section'}
            >
                {props.files.length > 0 ?
                    (
                        <div className={'category-images'}>
                            <div className={'image-cards'}>
                                {props.files.map((item) => (
                                    <ImageCard
                                        key={item.id}
                                        item={item}
                                        isAdmin={props.isAdmin}
                                        isSelected={isSelected(item.id)}
                                        imageUrl={getImageUrl(item, 'medium') || 'images/bg9.jpg'}
                                        onPreview={() => handlePreviewModalOpen(item)}
                                        onToggleSelect={toggleCheckBoxState}
                                        onDownload={props.handleDownload}
                                        onDelete={props.handleDelete}
                                        onShowDetails={handleShowDetails}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <EmptyData
                            icon={<img
                                src={currentState.iconSrc}
                                alt="Empty state icon"
                                className="media-empty-state-image"
                            />}
                            title={currentState.title}
                            subtitle={currentState.description}
                            button={
                                user.isAdmin &&
                                <Button
                                    onClick={handleMediaUploadOpen}
                                    type={"primary"}
                                    className="media-empty-state__action"
                                >
                                    {currentState.action}
                                </Button>
                            }
                        />
                    )
                }
            </ScrollPagination>

            <UploadDocumentModal
                open={state.uploadFileModal.isOpen}
                showDocumentList={false}
                allowMultiple={true}
                documentType={docType as keyof typeof Document_Type}
                documentPurpose={'GALLERY'}
                handleModalClose={handleMediaUploadClose}
                handleUploadedFileRes={props.handleUploadedMedia}
            />

            {state.previewModal.imgDetails && <ImagePreviewModal
                isDeleteBtnVisible={user.isAdmin}
                onDownload={props.handleDownload}
                onDelete={props.handleDelete}
                handleModalClose={handlePreviewModalClose}
                goToNextImage={goToNextImage}
                goToPreviousImage={goToPreviousImage}
                showInfo={state.previewModal.showInfo}
                isOpen={state.previewModal.isOpen}
                imgDetails={state.previewModal.imgDetails}
                UIMetaFields={props.UIMetaFields}
            />}
        </div>
    )
}

export default MediaCollectionView;
